import { DataGridProProps } from '@mui/x-data-grid-pro';
import { useCustomSnackbar } from '@procureai/ui-mui';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { TsrctCompetitiveBiddingVariant, TsrctDemand, TsrctDemandNegotiationCase } from '@/generated/gql/graphql';
import { useQueryPollingUntil } from '~/hooks';
import { RouteId } from '~/routes';

import { DEMAND_CASES_QUERY } from './DemandCasesQuery.gql';
import { DemandCasesDataGridRow } from './DemandCasesWidget.types';

export interface UseDemandCasesWidgetProps {
  demandId: string;
  t: (key: string) => string;
}

export interface UseDemandCasesWidgetResult {
  isActiveCompetitiveBidding?: boolean;
  loading?: boolean;
  status?: TsrctDemand['status'];
  rows: DemandCasesDataGridRow[];
  onRowClick: DataGridProProps['onRowClick'];
}

const emptyRows: TsrctDemandNegotiationCase[] = [];

export const useDemandCasesWidget = ({ demandId, t }: UseDemandCasesWidgetProps): UseDemandCasesWidgetResult => {
  const { onError, enqueueErrorSnackbar } = useCustomSnackbar();
  const navigate = useNavigate();

  const { data, loading } = useQueryPollingUntil(DEMAND_CASES_QUERY, {
    variables: {
      id: demandId,
    },
    onError,
    onPollingTimeout: () => {
      enqueueErrorSnackbar(t('error.caseCreatePolling'));
    },
  });

  const rows = data?.tesseract.demand.negotiationCases.nodes ?? emptyRows;
  const competitiveBiddingRule = data?.tesseract.demand.competitiveBiddingRule;
  const isActiveCompetitiveBidding =
    competitiveBiddingRule?.variant !== TsrctCompetitiveBiddingVariant.NONE && !competitiveBiddingRule?.isEditable;

  const onRowClick: NonNullable<DataGridProProps['onRowClick']> = useCallback(
    (params) => {
      navigate(RouteId.negotiationDetail(params.row.id));
    },
    [navigate],
  );

  return {
    isActiveCompetitiveBidding,
    status: data?.tesseract.demand.status,
    rows,
    loading,
    onRowClick,
  };
};
